import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { InputOptions } from 'types/input'
import { formatDubaiDate } from 'utils/helpers'

export const FILTER_FIELD_STOCK_OUT_REPORT = 'stockOutReportFilters'

// Lifecycle filter. `NONE` clears the filter (handled by useLogFilters); the
// other values are sent to the server verbatim as `status=open|closed`.
export const STOCK_OUT_STATUS_OPTIONS: InputOptions[] = [
  { label: '--- NONE ---', value: 'NONE' },
  { label: 'Out of Stock', value: 'open' },
  { label: 'Back in Stock', value: 'closed' },
]

const textFormatter = (params: ValueFormatterParams) => params.value || '-'

// Quantities must NOT go through textFormatter: a stock-out row always carries
// 0, which is falsy and would render as '-'.
const qtyFormatter = (params: ValueFormatterParams) =>
  params.value === null || params.value === undefined
    ? '-'
    : String(params.value)

const dateFormatter = (params: ValueFormatterParams) =>
  params.value ? formatDubaiDate(params.value) : '-'

export const columns: ColDef[] = [
  {
    // Pinned so the state stays visible: with 14 data columns the grid scrolls
    // horizontally, and open/closed is the first thing anyone reads off a row.
    headerName: 'Status',
    colId: 'status',
    field: 'stockInDate',
    minWidth: 140,
    pinned: 'left',
    cellRenderer: (params: ICellRendererParams) => {
      const isBackInStock = Boolean(params.value)

      return (
        <span
          className={`rounded-lg px-2 py-1 text-sm ${
            isBackInStock
              ? 'bg-green-400 text-black'
              : 'bg-red-200 text-red-800'
          }`}
        >
          {isBackInStock ? 'Back in Stock' : 'Out of Stock'}
        </span>
      )
    },
  },
  {
    // `store` is the store code, not the name. For a SAP store code with no
    // store mapped to it, this holds the raw code exactly as SAP sent it.
    headerName: 'Store',
    field: 'store',
    minWidth: 130,
    valueFormatter: textFormatter,
  },
  {
    headerName: 'Barcode',
    field: 'barcode',
    minWidth: 140,
    valueFormatter: textFormatter,
  },
  {
    headerName: 'Dept',
    field: 'dept',
    minWidth: 110,
    valueFormatter: textFormatter,
  },
  {
    headerName: 'Section',
    field: 'section',
    minWidth: 110,
    valueFormatter: textFormatter,
  },
  {
    headerName: 'Family',
    field: 'family',
    minWidth: 110,
    valueFormatter: textFormatter,
  },
  {
    headerName: 'SAP Dept',
    field: 'sapDept',
    minWidth: 120,
    valueFormatter: textFormatter,
  },
  {
    headerName: 'SAP Section',
    field: 'sapSection',
    minWidth: 130,
    valueFormatter: textFormatter,
  },
  {
    headerName: 'SAP Family',
    field: 'sapFamily',
    minWidth: 130,
    valueFormatter: textFormatter,
  },
  {
    headerName: 'Stock-out Inv Value',
    field: 'stockOutInvValue',
    minWidth: 170,
    type: 'numericColumn',
    valueFormatter: qtyFormatter,
  },
  {
    headerName: 'Stock-out Date',
    field: 'stockOutDate',
    minWidth: 150,
    valueFormatter: dateFormatter,
  },
  {
    // Pre-formatted Dubai clock time captured at write time, so the grid and
    // the CSV always agree.
    headerName: 'Stock-out Time',
    field: 'stockOutTime',
    minWidth: 140,
    valueFormatter: textFormatter,
  },
  {
    // Blank, not 0, while the entry is still open — "never came back in stock"
    // has to stay distinguishable from "came back with 0".
    headerName: 'Stock-in Inv Value',
    field: 'stockInInvValue',
    minWidth: 170,
    type: 'numericColumn',
    valueFormatter: qtyFormatter,
  },
  {
    headerName: 'Stock-in Date',
    field: 'stockInDate',
    minWidth: 150,
    valueFormatter: dateFormatter,
  },
  {
    headerName: 'Stock-in Time',
    field: 'stockInTime',
    minWidth: 140,
    valueFormatter: textFormatter,
  },
]
